import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useServices } from "@/app/use-services";
import { useMomentOnce } from "@/app/use-moment";
import type { Failure } from "@/lib/analytics/moments";
import { ApiError, errorMessage } from "@/lib/api/errors";
import { isLifeReadingName, type LifeReading } from "@/lib/api/models/life";
import { BackLink } from "@/ui/BackLink";
import { Button, LinkButton } from "@/ui/Button";
import { ErrorView } from "@/ui/ErrorView";
import { Page } from "@/ui/Page";
import { Row } from "@/ui/Row";
import { Sheet } from "@/ui/Sheet";
import { Body, Display, Label, Mono, Tiny } from "@/ui/Text";
import { Paywall } from "@/features/plan/Paywall";
import { outcomeFor, wallForOutcome } from "@/features/plan/paywall-copy";
import { NO_PROFILE_LINE } from "./keys";

const EMPTY_READING =
  "The engine returned nothing for this one. We would rather show you nothing than write something that was never computed.";

/**
 * One reading, always the server's. Three failures are not the same kind:
 * 402 is the wall, 404 is "no birth details yet", everything else is a fault
 * with a retry. A plausible-looking fallback reading is the exact thing this
 * product exists not to be.
 */
export function LifeReadingPage() {
  const { reading = "" } = useParams();
  const { api, analytics } = useServices();
  const navigate = useNavigate();
  const [showWork, setShowWork] = useState(false);
  const valid = isLifeReadingName(reading);
  const outcome = outcomeFor(reading);
  const query = useQuery<LifeReading>({
    queryKey: ["me", "life", reading],
    queryFn: () => (valid ? api.fetchLifeReading(reading) : Promise.reject(ApiError.badResponse())),
    enabled: valid,
    retry: false,
  });
  useMomentOnce(query.isSuccess ? { name: "reading viewed", reading } : null);
  useMomentOnce(query.isError ? { name: "reading failed", reading, reason: failureOf(query.error) } : null);

  const head = (
    <>
      <BackLink to="/life" label="Life" />
      <Label className="mt-3">{outcome?.row ?? reading}</Label>
    </>
  );

  if (!valid) {
    return <Page>{head}<Body className="mt-3">There is no reading by that name.</Body></Page>;
  }
  if (query.isPending) return <Page>{head}<Body className="mt-3">Working out your dates…</Body></Page>;
  if (query.isError) {
    const error = query.error;
    if (error instanceof ApiError && error.status === 402 && outcome) {
      return (
        <Page>
          {head}
          <Paywall copy={wallForOutcome(outcome)} onClose={() => navigate("/life")} />
        </Page>
      );
    }
    if (error instanceof ApiError && error.isNotFound) return <Page>{head}<Body className="mt-3">{NO_PROFILE_LINE}</Body></Page>;
    return <Page>{head}<ErrorView message={errorMessage(error)} onRetry={() => void query.refetch()} /></Page>;
  }

  const data = query.data;
  const hasContent = Boolean(data.headline || data.windows.length || data.notes.length);
  if (!hasContent) return <Page>{head}<Body className="mt-3">{EMPTY_READING}</Body></Page>;
  const hasWorking = data.facts_used.length > 0 || data.sources.length > 0;

  return (
    <Page foot={hasWorking ? <Button
            label="Show the work"
            outlined
            onClick={() => {
              analytics.track({ name: "reading work opened", reading });
              setShowWork(true);
            }}
          /> : undefined}>
      {head}
      <Display className="mt-3 text-[27px]">{data.headline}</Display>
      {data.framing && (
        <div className="mt-3.5 border border-rule px-3 py-2.5">
          <Tiny>{data.framing}</Tiny>
        </div>
      )}
      {data.windows.length > 0 && (
        <>
          <Label className="mt-5">The dates</Label>
          {data.windows.map((w) => (
            <div key={`${w.level}-${w.start_utc}-${w.label}`} className="border-b border-rule py-3.5">
              <div className="flex items-baseline gap-2.5">
                <span className="font-display text-[20px] leading-tight">{w.label}</span>
                {w.running && <span className="label text-ink">Open now</span>}
              </div>
              {w.what && <Tiny className="mt-1">{w.what}</Tiny>}
            </div>
          ))}
        </>
      )}
      {data.notes.length > 0 && (
        <div className="mt-5 space-y-2.5">
          {data.notes.map((note) => <Body key={note}>{note}</Body>)}
        </div>
      )}
      <Sheet open={showWork} onClose={() => setShowWork(false)} label="Why this">
        <Label>Why this</Label>
        <Display size="md" as="h2" className="mt-2">Nothing here was invented</Display>
        {data.facts_used.length > 0 && (
          <>
            <Label className="mt-4">Computed from</Label>
            <ul className="mt-1 space-y-1">
              {data.facts_used.map((f) => <li key={f}><Mono>· {f}</Mono></li>)}
            </ul>
          </>
        )}
        {data.sources.length > 0 && <Row label="Sources" value={data.sources.join(" · ")} serif={false} />}
        <Row label="Engine" value={data.engine_version ? `Swiss Ephemeris · Lahiri · ${data.engine_version}` : "Swiss Ephemeris · Lahiri"} serif={false} />
        <div className="mt-3 text-center"><LinkButton label="Close" onClick={() => setShowWork(false)} /></div>
      </Sheet>
    </Page>
  );
}

/** Why a reading did not come back, in the words analytics keeps. */
function failureOf(error: unknown): Failure {
  if (error instanceof ApiError && error.status === 402) return "no_time_left";
  if (error instanceof ApiError && error.isNotFound) return "not_found";
  return "error";
}
